// src/components/VaccineInfographic.jsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import ReactFlow, {
  ReactFlowProvider,
  useReactFlow,
  Controls,
  Background,
  Panel,
} from "reactflow";

import {
  Box, Text, Heading} from "@chakra-ui/react";
import { MiniMap } from "@reactflow/minimap";
import { Switch, FormLabel } from "@chakra-ui/react";
import "reactflow/dist/style.css";
import "@reactflow/minimap/dist/style.css";
import "@reactflow/controls/dist/style.css";

import CustomNode from "./CustomNode";

// ====================================================================================
// --- ส่วนที่ 1: Helper Functions & Logic การแปลงข้อมูล (ปรับปรุงให้ถูกต้อง) ---
// ====================================================================================

const parseWarehouseName = (name) => {
  if (name && name.includes("จังหวัด")) {
    return name.replace("คลังวัคซีนสำนักงานปศุสัตว์จังหวัด", "");
  }
  return name;
};

const livestockTypeNames = {
  CATTLE: "โค-กระบือ",
  PIG: "สุกร",
  POULTRY: "สัตว์ปีก",
  OTHERS: "อื่นๆ",
};

export const processAndStructureData = (apiData) => {
  if (
    !apiData ||
    !apiData.InventoryReport ||
    apiData.InventoryReport.length === 0
  )
    return null;
  const inventory = apiData.InventoryReport;

  const uniqueRegionIDs = new Set(
    inventory.map((item) => item.RegionID).filter((id) => id != null)
  );
  const uniqueWarehouseIDs = new Set(inventory.map((item) => item.WarehouseID));

  let detectedRole = "CENTRAL";
  let roleMetadata = { name: "ภาพรวมทั้งหมด" };

  if (uniqueWarehouseIDs.size === 1) {
    detectedRole = "PROVINCE";
    const warehouseId = uniqueWarehouseIDs.values().next().value;
    const warehouseName = inventory.find(
      (item) => item.WarehouseID === warehouseId
    )?.WarehouseName;
    roleMetadata = {
      id: warehouseId,
      name: warehouseName || `คลัง ${warehouseId}`,
    };
  }
  // 2. ถ้าไม่ใช่ PROVINCE, ค่อยตรวจสอบว่าเป็น REGION หรือไม่
  else if (uniqueRegionIDs.size === 1) {
    detectedRole = "REGION";
    const regionId = uniqueRegionIDs.values().next().value;
    const regionName = inventory.find(
      (item) => item.RegionID === regionId
    )?.RegionName;
    roleMetadata = { id: regionId, name: regionName || `เขต ${regionId}` };
  }

  const livestockMap = new Map();
  inventory.forEach((item) => {
    const livestockType = item.LivestockType || "OTHERS";
    if (!livestockMap.has(livestockType))
      livestockMap.set(livestockType, {
        id: livestockType,
        name: livestockTypeNames[livestockType] || "อื่นๆ",
        total: 0,
        vaccines: new Map(),
      });
    const currentLivestock = livestockMap.get(livestockType);
    currentLivestock.total += item.TotalQuantity;

    if (!currentLivestock.vaccines.has(item.VaccineID))
      currentLivestock.vaccines.set(item.VaccineID, {
        id: item.VaccineID,
        name: item.VaccineNameTH,
        total: 0,
        regions: new Map(),
      });
    const currentVaccine = currentLivestock.vaccines.get(item.VaccineID);
    currentVaccine.total += item.TotalQuantity;

    const regionKey = item.RegionID || item.WarehouseID;
    const regionName = item.RegionName || item.WarehouseName;
    if (!currentVaccine.regions.has(regionKey)) {
      const isTerminal = !item.RegionID;
      currentVaccine.regions.set(regionKey, {
        id: `${item.VaccineID}-${regionKey}`,
        name: regionName,
        total: 0,
        warehouses: [],
        isTerminal: isTerminal,
      });
    }
    const currentRegion = currentVaccine.regions.get(regionKey);
    currentRegion.total += item.TotalQuantity;
    if (item.RegionID)
      currentRegion.warehouses.push({
        id: item.WarehouseID,
        name: parseWarehouseName(item.WarehouseName),
        total: item.TotalQuantity,
      });
  });

  const grandTotal = inventory.reduce(
    (sum, item) => sum + item.TotalQuantity,
    0
  );

  const rootLabel =
    detectedRole === "CENTRAL"
      ? "วัคซีนคงคลังทั้งหมด"
      : `ยอดรวมของ ${
          detectedRole === "PROVINCE"
            ? parseWarehouseName(roleMetadata.name)
            : roleMetadata.name
        }`;

  let livestockTypes;

  switch (detectedRole) {
    case "REGION":
      livestockTypes = Array.from(livestockMap.values())
        .sort((a, b) => a.name.localeCompare(b.name, "th"))
        .map((livestock) => {
          livestock.vaccines = Array.from(livestock.vaccines.values())
            .sort((a, b) => a.name.localeCompare(b.name, "th"))
            .map((vax) => {
              const provincesInRegion = [];
              vax.regions.forEach((region) => {
                provincesInRegion.push(...region.warehouses);
              });
              vax.provinces = provincesInRegion.sort((a, b) =>
                a.name.localeCompare(b.name, "th")
              );
              vax.provinces.forEach((p) => (p.isTerminal = true));
              delete vax.regions;
              return vax;
            });
          return livestock;
        });
      break;
    case "PROVINCE":
      livestockTypes = Array.from(livestockMap.values())
        .sort((a, b) => a.name.localeCompare(b.name, "th"))
        .map((livestock) => {
          livestock.vaccines = Array.from(livestock.vaccines.values()).sort(
            (a, b) => a.name.localeCompare(b.name, "th")
          );
          livestock.vaccines.forEach((vax) => {
            vax.isTerminal = true;
            delete vax.regions;
          });
          return livestock;
        });
      break;
    case "CENTRAL":
    default:
      livestockTypes = Array.from(livestockMap.values())
        .sort((a, b) => a.name.localeCompare(b.name, "th"))
        .map((livestock) => {
          livestock.vaccines = Array.from(livestock.vaccines.values())
            .sort((a, b) => a.name.localeCompare(b.name, "th"))
            .map((vax) => {
              vax.regions = Array.from(vax.regions.values()).sort((a, b) =>
                a.name.localeCompare(b.name, "th")
              );
              vax.regions.forEach((region) => {
                if (region.warehouses)
                  region.warehouses.sort((a, b) =>
                    a.name.localeCompare(b.name, "th")
                  );
              });
              return vax;
            });
          return livestock;
        });
      break;
  }

  const hierarchy = { name: rootLabel, total: grandTotal, livestockTypes };
  return { role: detectedRole, hierarchy };
};

const nodeWidth = 280;
const nodeHeight = 130;
const X_SPACING_LEVELS = 520;
const Y_SPACING_SIBLINGS = 150;

const calculateCustomLayout = (appData, expandedNodes) => {
  if (!appData || !appData.hierarchy) return {};
  const { role, hierarchy } = appData;
  const positions = {};
  let currentX = 0;

  positions["total"] = { x: currentX, y: 0 };
  currentX += X_SPACING_LEVELS;

  const livestockTypes = hierarchy.livestockTypes || [];
  const totalHeightLivestock = (livestockTypes.length - 1) * Y_SPACING_SIBLINGS;
  const startYLivestock = -totalHeightLivestock / 2;

  livestockTypes.forEach((livestock, index) => {
    const livestockY = startYLivestock + index * Y_SPACING_SIBLINGS;
    positions[livestock.id] = { x: currentX, y: livestockY };
    if (expandedNodes.has(livestock.id)) {
      const vaccines = livestock.vaccines || [];
      const totalHeightVaccine = (vaccines.length - 1) * Y_SPACING_SIBLINGS;
      const startYVaccine = livestockY - totalHeightVaccine / 2;
      vaccines.forEach((vaccine, vIndex) => {
        const vaccineY = startYVaccine + vIndex * Y_SPACING_SIBLINGS;
        positions[vaccine.id] = { x: currentX + X_SPACING_LEVELS, y: vaccineY };
        if (expandedNodes.has(vaccine.id) && !vaccine.isTerminal) {
          if (role === "REGION") {
            // FIX: แก้ไขการอ้างอิงจาก warehouses เป็น provinces ให้ถูกต้อง
            const provinces = vaccine.provinces || [];
            const totalHeightProvince =
              (provinces.length - 1) * Y_SPACING_SIBLINGS;
            const startYProvince = vaccineY - totalHeightProvince / 2;
            provinces.forEach((province, pIndex) => {
              const provinceY = startYProvince + pIndex * Y_SPACING_SIBLINGS;
              positions[province.id] = {
                x: currentX + X_SPACING_LEVELS * 2,
                y: provinceY,
              };
            });
          } else {
            // CENTRAL Role
            const regions = vaccine.regions || [];
            const totalHeightRegion = (regions.length - 1) * Y_SPACING_SIBLINGS;
            const startYRegion = vaccineY - totalHeightRegion / 2;
            regions.forEach((region, rIndex) => {
              const regionY = startYRegion + rIndex * Y_SPACING_SIBLINGS;
              positions[region.id] = {
                x: currentX + X_SPACING_LEVELS * 2,
                y: regionY,
              };
              const summaryNodeId = `summary-${region.id}`;
              positions[summaryNodeId] = {
                x: currentX + X_SPACING_LEVELS * 3,
                y: regionY,
              };
              if (expandedNodes.has(region.id) && !region.isTerminal) {
                const warehouses = region.warehouses || [];
                const totalHeightWarehouse =
                  (warehouses.length - 1) * Y_SPACING_SIBLINGS;
                const startYWarehouse = regionY - totalHeightWarehouse / 2;
                warehouses.forEach((warehouse, wIndex) => {
                  const warehouseY =
                    startYWarehouse + wIndex * Y_SPACING_SIBLINGS;
                  positions[warehouse.id] = {
                    x: currentX + X_SPACING_LEVELS * 3,
                    y: warehouseY,
                  };
                });
              }
            });
          }
        }
      });
    }
  });
  return positions;
};

const Flow = () => {
  const { sessionId } = useAuth();
  const { fitView, setCenter } = useReactFlow();
  const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [appData, setAppData] = useState(null);
  const [isSummaryMode, setIsSummaryMode] = useState(false);
  const [viewportAction, setViewportAction] = useState(null);
  const [selectedPath, setSelectedPath] = useState({
    nodes: new Set(),
    edges: new Set(),
    category: null,
  });
  const [hoveredPath, setHoveredPath] = useState({
    nodes: new Set(),
    edges: new Set(),
    category: null,
  });
  const [headerText, setHeaderText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchVaccineInventorySummary = useCallback(async () => {
    if (!sessionId) return;
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineInventorySummaryWithRegion",
        sessionId,
      });
      if (response.result === "success") {
        const processed = processAndStructureData(response);
        setAppData(processed);
        setViewportAction({ type: "INITIAL_VIEW" });
      } else {
        setAppData(null);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchVaccineInventorySummary();
  }, [fetchVaccineInventorySummary]);

  // ในไฟล์ VaccineInfographic.js

  const onNodeClick = useCallback(
    (event, clickedNode) => {
      if (!appData) return;

      // --- ส่วนที่ 1: จัดการ Expanded Nodes (เหมือนเดิม) ---
      const parentIdMap = new Map();
      edges.forEach((edge) => {
        parentIdMap.set(edge.target, edge.source);
      });

      const newExpandedNodes = new Set();
      let currentIdForPath = clickedNode.id;
      const isClosing =
        expandedNodes.has(clickedNode.id) &&
        (parentIdMap.has(clickedNode.id) ||
          clickedNode.id.startsWith("summary-"));

      if (isClosing) {
        currentIdForPath = parentIdMap.get(clickedNode.id);
      }
      while (currentIdForPath) {
        newExpandedNodes.add(currentIdForPath);
        currentIdForPath = parentIdMap.get(currentIdForPath);
      }
      setExpandedNodes(newExpandedNodes);

      // --- ส่วนที่ 2: จัดการ Viewport Action และ Highlight ---

      if (isClosing) {
        const parentId = parentIdMap.get(clickedNode.id);
        setViewportAction({
          type: "ZOOM_TO_NODE",
          nodeId: parentId || "total",
        });
      } else {
        let childrenCount = 0;
        const { type, livestockCategory } = clickedNode.data;
        const { role, hierarchy } = appData;

        // --- ส่วนที่แก้ไข ---
        if (type === "ประเภทสัตว์") {
          const livestock = hierarchy.livestockTypes.find(
            (ls) => ls.id === clickedNode.id
          );
          childrenCount = livestock ? livestock.vaccines.length : 0;
        } else if (type === "วัคซีน") {
          const livestock = hierarchy.livestockTypes.find(
            (ls) => ls.id === livestockCategory
          );
          const vaccine = livestock?.vaccines.find(
            (v) => v.id === clickedNode.id
          );

          // FIX: เพิ่มเงื่อนไขสำหรับ PROVINCE role โดยเฉพาะ
          if (role === "PROVINCE") {
            childrenCount = 0; // User จังหวัด, วัคซีนเป็นปลายทางเสมอ
          } else if (role === "REGION") {
            childrenCount = vaccine ? vaccine.provinces.length : 0;
          } else {
            // CENTRAL role
            childrenCount = vaccine ? vaccine.regions.length : 0;
          }
        } else if (type === "เขต" || type === "คลังพิเศษ") {
          const livestock = hierarchy.livestockTypes.find(
            (ls) => ls.id === livestockCategory
          );
          const vaccine = livestock?.vaccines.find((v) =>
            v.regions.some((r) => r.id === clickedNode.id)
          );
          const region = vaccine?.regions.find((r) => r.id === clickedNode.id);
          childrenCount = isSummaryMode
            ? 1
            : region
            ? region.warehouses.length
            : 0;
        }

        setViewportAction({
          type: "ZOOM_TO_NODE",
          nodeId: clickedNode.id,
          childrenCount,
        });
      }

      // --- ส่วนที่ 3: จัดการ Highlight (ย้ายมาไว้รวมกันตรงนี้) ---
      const nodesToHighlight = newExpandedNodes;
      const edgesToHighlight = new Set();
      nodesToHighlight.forEach((nodeId) => {
        const parentId = parentIdMap.get(nodeId);
        if (parentId) {
          edgesToHighlight.add(`e-${parentId}-${nodeId}`);
        }
      });
      const category = isClosing ? null : clickedNode.data.livestockCategory;
      setSelectedPath({
        nodes: nodesToHighlight,
        edges: edgesToHighlight,
        category: category,
      });
    },
    [expandedNodes, edges, appData, isSummaryMode]
  );

  const onNodeMouseEnter = useCallback(
    (event, node) => {
      const parentIdMap = new Map();
      edges.forEach((edge) => parentIdMap.set(edge.target, edge.source));

      const nodesToHighlight = new Set();
      const edgesToHighlight = new Set();
      let currentNodeId = node.id;

      while (currentNodeId) {
        nodesToHighlight.add(currentNodeId);
        const parentId = parentIdMap.get(currentNodeId);
        if (parentId) edgesToHighlight.add(`e-${parentId}-${currentNodeId}`);
        currentNodeId = parentId;
      }
      setHoveredPath({
        nodes: nodesToHighlight,
        edges: edgesToHighlight,
        category: node.data.livestockCategory,
      });
    },
    [edges]
  );

  const onNodeMouseLeave = useCallback(() => {
    setHoveredPath({ nodes: new Set(), edges: new Set(), category: null });
  }, []);

  useEffect(() => {
    if (!viewportAction || nodes.length === 0) return;
    setTimeout(() => {
      const targetNode = nodes.find((n) => n.id === viewportAction.nodeId);
      switch (viewportAction.type) {
        case "INITIAL_VIEW":
          fitView({ duration: 400, padding: 0.2 });
          break;
        case "ZOOM_TO_NODE":
          if (targetNode) {
            let targetZoom = 1.0;
            const { childrenCount } = viewportAction;
            if (childrenCount > 15) targetZoom = 0.3;
            else if (childrenCount > 10) targetZoom = 0.4;
            else if (childrenCount > 5) targetZoom = 0.6;
            else if (childrenCount > 2) targetZoom = 0.8;
            const x = targetNode.position.x + nodeWidth / 2;
            const y = targetNode.position.y + nodeHeight / 2;
            setCenter(x, y, { zoom: targetZoom, duration: 600 });
          } else {
            fitView({ duration: 400, padding: 0.1 });
          }
          break;
        case "DEFAULT_FIT_VIEW":
        default:
          fitView({ duration: 400, padding: 0.1 });
          break;
      }
      setViewportAction(null);
    }, 50);
  }, [viewportAction, nodes, setCenter, fitView]);

 /* useEffect(() => {
    const { role } = appData;
    let head = "";
    switch (role) {
      case "REGION":
        setHeaderText("ผังแสดงวัคซีนคงคลัง ของสำนักงานปศุสัตว์");
        break;
      case "PROVINCE":
        setHeaderText("ผังแสดงวัคซีนคงคลัง จังหวัด");
        break;
      case "CENTRAL":
        setHeaderText("ผังแสดงวัคซีนคงคลังทั้งหมด");
        break;
      default:
        setHeaderText("ผังแสดงวัคซีนคงคลัง");
        break;
      break;
    }
  }, [appData]);*/

  useEffect(() => {
    if (!appData) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const { role, hierarchy } = appData;
    const nodePositions = calculateCustomLayout(appData, expandedNodes);

    const newNodes = [];
    const newEdges = [];
    const categoryColors = {
      CATTLE: "#3182CE",
      PIG: "#D53F8C",
      POULTRY: "#DD6B20",
      OTHERS: "#718096",
    };
    // ใน useEffect หลัก, แก้ไขฟังก์ชัน createEdge

    const createEdge = (sourceId, targetId, livestockCategory) => {
      const edgeId = `e-${sourceId}-${targetId}`;

      // ตรวจสอบทั้ง 2 สถานะ
      const isSelected = selectedPath.edges.has(edgeId);
      const isHovered = hoveredPath.edges.has(edgeId);

      const categoryColors = {
        CATTLE: "#3182CE",
        PIG: "#D53F8C",
        POULTRY: "#DD6B20",
        OTHERS: "#718096",
      };

      // กำหนด style เริ่มต้น (สภาพปกติ)
      const style = {
        stroke: "#CBD5E0", // สีเทาอ่อน
        strokeWidth: 1.5,
        opacity: 0.7,
        transition: "all 0.2s", // เพิ่ม transition เพื่อความสวยงาม
      };

      // ถ้ากำลังถูก hover (แต่ยังไม่ถูก select)
      if (isHovered && !isSelected) {
        style.stroke = categoryColors[hoveredPath.category] || "#A0AEC0";
        style.strokeWidth = 2.5;
        style.opacity = 1;
      }

      // ถ้าถูก select (สำคัญที่สุด จะทับ style ของ hover)
      if (isSelected) {
        style.stroke = categoryColors[selectedPath.category] || "#A0AEC0";
        style.strokeWidth = 2.5;
        style.opacity = 1;
        style.strokeDasharray = "6 3"; // เป็นเส้นประ
      }

      return {
        id: edgeId,
        source: sourceId,
        target: targetId,
        animated: isSelected, // ให้เส้นเคลื่อนไหวเฉพาะตอนที่ถูกเลือก (เป็นเส้นประวิ่ง)
        style: style,
        zIndex: isSelected ? 100 : isHovered ? 50 : 0,
      };
    };

    newNodes.push({
      id: "total",
      type: "custom",
      data: {
        label: hierarchy.name,
        stock: hierarchy.total,
        type: "ทั้งหมด",
        isHighlighted: selectedPath.nodes.has("total"),
      },
      position: nodePositions["total"],
    });

    hierarchy.livestockTypes.forEach((livestock) => {
      const {
        id: livestockId,
        name,
        total,
        vaccines,
        id: livestockCategory,
      } = livestock;
      newNodes.push({
        id: livestockId,
        type: "custom",
        data: {
          label: name,
          stock: total,
          type: "ประเภทสัตว์",
          livestockCategory,
          isHighlighted: selectedPath.nodes.has(livestockId),
        },
        position: nodePositions[livestockId],
      });
      newEdges.push(createEdge("total", livestockId, livestockCategory));

      if (expandedNodes.has(livestockId)) {
        (vaccines || []).forEach((vaccine) => {
          const {
            id: vaccineId,
            name,
            total,
            isTerminal: isVaccineTerminal,
          } = vaccine;
          newNodes.push({
            id: vaccineId,
            type: "custom",
            data: {
              label: name,
              stock: total,
              type: "วัคซีน",
              livestockCategory,
              isHighlighted: selectedPath.nodes.has(vaccineId),
              isTerminal: isVaccineTerminal,
            },
            position: nodePositions[vaccineId],
          });
          newEdges.push(createEdge(livestockId, vaccineId, livestockCategory));

          if (expandedNodes.has(vaccineId) && !isVaccineTerminal) {
            if (role === "REGION") {
              // FIX: แก้ไขการอ้างอิงจาก .warehouses เป็น .provinces
              (vaccine.provinces || []).forEach((province) => {
                newNodes.push({
                  id: province.id,
                  type: "custom",
                  data: {
                    label: province.name,
                    stock: province.total,
                    type: "จังหวัด",
                    livestockCategory,
                    isHighlighted: selectedPath.nodes.has(province.id),
                    isTerminal: true,
                  },
                  position: nodePositions[province.id],
                });
                newEdges.push(
                  createEdge(vaccineId, province.id, livestockCategory)
                );
              });
            } else {
              // CENTRAL Role
              (vaccine.regions || []).forEach((region) => {
                const {
                  id: regionId,
                  name,
                  total,
                  warehouses,
                  isTerminal,
                } = region;
                newNodes.push({
                  id: regionId,
                  type: "custom",
                  data: {
                    label: name,
                    stock: total,
                    type: isTerminal ? "คลังพิเศษ" : "เขต",
                    livestockCategory,
                    isHighlighted: selectedPath.nodes.has(regionId),
                    isTerminal,
                  },
                  position: nodePositions[regionId],
                });
                newEdges.push(
                  createEdge(vaccineId, regionId, livestockCategory)
                );

                if (expandedNodes.has(regionId) && !isTerminal) {
                  if (isSummaryMode) {
                    const summaryNodeId = `summary-${regionId}`;
                    newNodes.push({
                      id: summaryNodeId,
                      type: "custom",
                      data: {
                        label: `จังหวัดใน ${region.name}`,
                        type: "จังหวัดสรุป",
                        provinces: warehouses,
                        livestockCategory,
                        isTerminal: true,
                      },
                      position: nodePositions[summaryNodeId],
                    });
                    newEdges.push(
                      createEdge(regionId, summaryNodeId, livestockCategory)
                    );
                  } else {
                    (warehouses || []).forEach((warehouse) => {
                      newNodes.push({
                        id: warehouse.id,
                        type: "custom",
                        data: {
                          label: warehouse.name,
                          stock: warehouse.total,
                          type: "จังหวัด",
                          livestockCategory,
                          isHighlighted: selectedPath.nodes.has(warehouse.id),
                          isTerminal: true,
                        },
                        position: nodePositions[warehouse.id],
                      });
                      newEdges.push(
                        createEdge(regionId, warehouse.id, livestockCategory)
                      );
                    });
                  }
                }
              });
            }
          }
        });
      }
    });
    setNodes(newNodes);
    setEdges(newEdges);
  }, [appData, expandedNodes, selectedPath, isSummaryMode]);

  if (isLoading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        กำลังโหลดข้อมูล...
      </div>
    );

  return (
    <div style={{ height: "100vh", width: "100%" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        minZoom={0.15}
        maxZoom={2}
      >
        <Panel 
          position="top-left" 
          style={{ 
            background: 'rgba(255, 255, 255, 0.9)', 
            padding: '10px 15px', 
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            boxShadow: 'md'
          }}
        >
          <Heading size="md" color="gray.700">
            {/* ดึงชื่อหัวข้อแบบไดนามิกมาจาก appData state */}
            {`สต๊อกวัคซีนคงเหลือ ระดับประเทศ | ระดับคลังกลาง(สทช.) | ระดับคลังเขต | ระดับคลังจังหวัด : ${appData?.hierarchy?.name || 'กำลังโหลด...'}`}
          </Heading>
        </Panel>
        <Controls />
        <Background />
      </ReactFlow>
    </div>
  );
};

const VaccineInfographic = () => (
  <ReactFlowProvider>
    <Flow />
  </ReactFlowProvider>
);

export default VaccineInfographic;
